<template>
    <BOffcanvas v-model="visible" placement="end" :title="$t('support.title')" body-class="d-flex flex-column">
        <!-- Liste des demandes -->
        <template v-if="view === 'list'">
            <button class="btn btn-primary mb-3" @click="startCreate()">
                <i class="mdi mdi-plus me-1"></i>{{ $t('support.newRequest') }}
            </button>
            <h6 class="text-muted text-uppercase mb-2">{{ $t('support.history') }}</h6>
            <p v-if="!requests.length" class="text-muted">{{ $t('support.empty') }}</p>
            <div
                v-for="request in requests"
                :key="request.id"
                class="support-request border rounded p-2 mb-2"
                @click="openRequest(request)"
            >
                <div class="d-flex align-items-center gap-2">
                    <i class="mdi" :class="kindIcon(request.kind)"></i>
                    <strong class="flex-grow-1 text-truncate">{{ request.subject }}</strong>
                    <span v-if="request.unread > 0" class="badge bg-danger rounded-pill">{{ request.unread }}</span>
                </div>
                <small class="text-muted">
                    {{ $t(`support.kinds.${request.kind}`) }} ·
                    {{ request.status === 'open' ? $t('support.statusOpen') : $t('support.statusClosed') }} ·
                    {{ formatDate(request.updated_at) }}
                </small>
            </div>
        </template>

        <!-- Nouvelle demande -->
        <template v-else-if="view === 'create'">
            <button class="btn btn-sm btn-link text-start p-0 mb-2" @click="view = 'list'">
                <i class="mdi mdi-arrow-left me-1"></i>{{ $t('support.back') }}
            </button>
            <form class="d-flex flex-column gap-2" @submit.prevent="submitCreate()">
                <select class="form-select" v-model="form.kind" required>
                    <option value="bug">{{ $t('support.kinds.bug') }}</option>
                    <option value="idea">{{ $t('support.kinds.idea') }}</option>
                    <option value="help">{{ $t('support.kinds.help') }}</option>
                </select>
                <input type="text" class="form-control" v-model="form.subject" :placeholder="$t('support.subject')" required maxlength="120">
                <textarea class="form-control" v-model="form.message" :placeholder="$t('support.message')" rows="6" required></textarea>
                <button type="submit" class="btn btn-primary" :disabled="sending">
                    <i class="mdi mdi-send me-1"></i>{{ $t('support.send') }}
                </button>
            </form>
        </template>

        <!-- Conversation -->
        <template v-else>
            <button class="btn btn-sm btn-link text-start p-0 mb-2" @click="backToList()">
                <i class="mdi mdi-arrow-left me-1"></i>{{ $t('support.back') }}
            </button>
            <div class="mb-2">
                <strong>{{ currentRequest.subject }}</strong>
                <small class="d-block text-muted">{{ $t(`support.kinds.${currentRequest.kind}`) }}</small>
            </div>
            <div ref="thread" class="support-thread flex-grow-1 overflow-auto mb-2">
                <div
                    v-for="message in messages"
                    :key="message.id"
                    class="support-bubble rounded p-2 mb-2"
                    :class="message.from_support ? 'from-support' : 'from-me'"
                >
                    <div class="support-bubble-body">{{ message.body }}</div>
                    <small class="text-muted">{{ formatDate(message.created_at) }}</small>
                </div>
            </div>
            <form class="d-flex gap-2" @submit.prevent="submitReply()">
                <input type="text" class="form-control" v-model="reply" :placeholder="$t('support.reply')" required>
                <button type="submit" class="btn btn-primary" :disabled="sending">
                    <i class="mdi mdi-send"></i>
                </button>
            </form>
        </template>
    </BOffcanvas>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import moment from 'moment';
import { BOffcanvas } from 'bootstrap-vue-next';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';

const userStore = useUserStore();

defineExpose({
    show,
});

const visible = ref(false);
const view = ref('list');
const requests = ref([]);
const currentRequest = ref(null);
const messages = ref([]);
const reply = ref('');
const sending = ref(false);
const form = ref({ kind: 'help', subject: '', message: '' });
const thread = ref(null);

async function show() {
    visible.value = true;
    view.value = 'list';
    await refreshRequests();
}

async function refreshRequests() {
    requests.value = await userStore.loadSupportRequests();
}

function startCreate() {
    form.value = { kind: 'help', subject: '', message: '' };
    view.value = 'create';
}

async function submitCreate() {
    sending.value = true;
    try {
        const request = await userStore.createSupportRequest(form.value.kind, form.value.subject, form.value.message);
        await refreshRequests();
        await openRequest(request);
    } catch (error) {
        showToast('error', error);
    } finally {
        sending.value = false;
    }
}

async function openRequest(request) {
    currentRequest.value = request;
    view.value = 'thread';
    messages.value = await userStore.loadSupportMessages(request.id);
    request.unread = 0;
    userStore.supportUnread = requests.value.reduce((sum, r) => sum + (r.unread || 0), 0);
    scrollThread();
}

async function backToList() {
    view.value = 'list';
    await refreshRequests();
}

async function submitReply() {
    if (!reply.value.trim()) return;
    sending.value = true;
    try {
        const message = await userStore.sendSupportMessage(currentRequest.value.id, reply.value.trim());
        messages.value.push(message);
        reply.value = '';
        scrollThread();
    } catch (error) {
        showToast('error', error);
    } finally {
        sending.value = false;
    }
}

function scrollThread() {
    nextTick(() => {
        if (thread.value) {
            thread.value.scrollTop = thread.value.scrollHeight;
        }
    });
}

function kindIcon(kind) {
    switch (kind) {
        case 'bug': return 'mdi-bug-outline text-danger';
        case 'idea': return 'mdi-lightbulb-outline text-warning';
        default: return 'mdi-lifebuoy text-primary';
    }
}

function formatDate(date) {
    return moment(date).format('DD/MM/YYYY HH:mm');
}

</script>

<style scoped>
.support-request {
    cursor: pointer;
    transition: background 0.15s ease;
}

.support-request:hover {
    background: rgba(97, 78, 222, 0.08);
}

.support-thread {
    min-height: 200px;
}

.support-bubble {
    max-width: 85%;
}

.support-bubble.from-me {
    background: rgba(97, 78, 222, 0.12);
    margin-left: auto;
}

.support-bubble.from-support {
    background: rgba(0, 0, 0, 0.06);
    margin-right: auto;
}

.support-bubble-body {
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
